import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Link,
  Divider,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { authService } from '../services/authService';
import BrandLogo from '../components/Brand/BrandLogo';

export const Login = () => {
  const [loginData, setLoginData] = useState({
    login: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, loginWithProvider, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state && location.state.from && location.state.from.pathname) || '/dashboard';

  useEffect(() => {
    if (location.state && location.state.message) {
      setInfo(location.state.message);
    }
  }, [location.state]);

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value,
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    try {
      await login(loginData);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderLogin = async (providerId) => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      await loginWithProvider(providerId);
      navigate(from, { replace: true });
    } catch (err) {
      if (err.code === 'LINK_REQUIRED') {
        setInfo(err.message);
        if (err.email) setLoginData((prev) => ({ ...prev, login: err.email }));
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setInfo('');
    if (!loginData.login) {
      setError('Enter your email address first, then click "Forgot password".');
      return;
    }
    try {
      await authService.requestPasswordReset(loginData.login);
      setInfo('If an account exists for that email, a password reset link has been sent.');
    } catch (err) {
      setError(err.message);
    }
  };

  // Show loading while checking authentication status
  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <BrandLogo height={64} />
        </Box>

        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent>
            <Typography variant="h6" align="center" sx={{ mt: 1 }}>
              Sign in to your account
            </Typography>

            {info && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {info}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLogin} sx={{ mt: 3 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="login"
                label="Email"
                name="login"
                type="email"
                autoComplete="email"
                autoFocus
                value={loginData.login}
                onChange={handleLoginChange}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={loginData.password}
                onChange={handleLoginChange}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }}>or</Divider>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={() => handleProviderLogin('google')}
              disabled={loading}
            >
              Continue with Google
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Link component="button" type="button" variant="body2" onClick={handleForgotPassword}>
                Forgot password?
              </Link>
              <Link component={RouterLink} to="/register" variant="body2">
                Register your company
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};
